import React, { Fragment, useEffect, useRef, useState } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { Pagination, Navigation, Autoplay } from "swiper/modules";
import { FaCheckCircle, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { Link, useParams } from "react-router-dom";
import TripsDetailsAPI from "../api/TripsDetailsAPI";
import { IoMdAddCircle } from "react-icons/io";
import { FaDownload } from "react-icons/fa";
import WhatsappImg from "../assets/media/whatsapp.d026940870fb334920b7.png";
import { SiGooglepay, SiPaytm, SiPhonepe } from "react-icons/si";
import ReviewPreview from "./ReviewPreview";

// Utility function to safely access nested object properties
const safeGet = (obj, path, defaultValue = null) => {
  try {
    return path.split(".").reduce((current, key) => {
      return current && current[key] !== undefined
        ? current[key]
        : defaultValue;
    }, obj);
  } catch {
    return defaultValue;
  }
};

/**
 * Utility function to format date from 'YYYY-MM-DD' or similar.
 * @param {string} dateString - The date string from the API (e.g., '2025-01-18').
 * @param {'full'|'month'|'month-year'} formatType - 'full' for DD-Mon-YYYY, 'month' for Mon, 'month-year' for Mon-YY.
 * @returns {string} The formatted date string.
 */
const formatDate = (dateString, formatType) => {
  if (!dateString || dateString.toLowerCase() === "tbd")
    return formatType === "full" ? "TBD" : "TBD";

  try {
    const date = new Date(dateString);
    // Use UTC date to avoid local timezone shifts changing the date
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);

    if (isNaN(utcDate.getTime())) return formatType === "full" ? "TBD" : "TBD";

    // Get date parts from UTC date object
    const day = utcDate.getDate().toString().padStart(2, "0");
    // Use toLocaleDateString for the short month name ('Jan')
    const month = utcDate.toLocaleDateString("en-US", {
      month: "short",
      timeZone: "UTC",
    });
    const fullYear = utcDate.getFullYear().toString(); // Full year (YYYY)
    // const shortYear = fullYear.slice(-2); // Short year (YY)
    const shortYear = fullYear; // Short year (YY)

    if (formatType === "full") {
      return `${day}-${month}-${fullYear}`; // e.g., 18-Jan-2025
    } else if (formatType === "month-year") {
      return `${month}-${shortYear}`; // e.g., Oct-25
    }
    return month; // Fallback to 'month' format (Mon)
  } catch (e) {
    console.error("Date formatting error:", e);
    return "TBD"; // Generic TBD fallback
  }
};

// Safe HTML renderer component
const SafeHTML = ({ html, fallback = "No content available", style = {} }) => {
  if (!html || html.trim() === "" || html === null || html === undefined) {
    return (
      <div className="text-muted" style={style}>
        {fallback}
      </div>
    );
  }
  return <div dangerouslySetInnerHTML={{ __html: html }} style={style} />;
};

const TripsDetails = () => {
  // Removed allTrips state and related logic as pricing is now correctly sourced from tripsPackagesDetails
  // const [allTrips, setAllTrips] = useState([]);
  const [selectedPricing, setSelectedPricing] = useState("full");
  const [isShortDescriptionOpen, setIsShortDescriptionOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { slug } = useParams();

  let [activeTab, setActiveTab] = useState("Overview");
  let [tripsPackagesDetails, setTripsPackagesDetails] = useState(null);

  const [error, setError] = useState(null);
  const [tripsDetailsGallery, setTripsDetailsGallery] = useState([]);

  const toggleShortDescription = () => {
    setIsShortDescriptionOpen(!isShortDescriptionOpen);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) {
        setError("No trip identifier provided");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const res = await TripsDetailsAPI.getTripBySlug({ slug });

        if (res && res.data) {
          setTripsPackagesDetails(res.data);
          setError(null);
        } else {
          setError("No trip data found");
        }
      } catch (err) {
        console.error("Error:", err);
        setError("Unable to load trip data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  // Safe destructuring with defaults
  const {
    batches = [],
    exclusive = [],
    galleryimage = [],
    inclusive = [],
    itinerary,
    overview,
    short_description,
    ["terms-condition"]: termsCondition,
    title,
    pdf_file,
    privacy_policy,
    booking_amount: bookingAmount = 0,
    full_package_amount: fullPackageAmount = 0,
    discounted_amount: discountedAmount = 0,
  } = tripsPackagesDetails || {};

  const handlePdfDownload = () => {
    if (!pdf_file || pdf_file.trim() === "") {
      alert("PDF file is not available for download");
      return;
    }

    try {
      const fullPdfUrl = `https://sortedtrips.in/admin/${pdf_file}`;
      const link = document.createElement("a");
      link.href = fullPdfUrl;
      link.target = "_blank";
      link.download = `${title || "trip_itinerary"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF. Please try again later.");
    }
  };

  let handleTripsDetailsButton = (data) => {
    switch (data) {
      case "Overview":
        return (
          <div className="custom-trips-details-overview px-3">
            <div className="ps-0">
              <div
                style={{ fontFamily: "nexaLight", width: "100%" }}
                dangerouslySetInnerHTML={{ __html: overview }}
              ></div>

              {/* Accordion for Short Description - Only show if short_description exists */}
              {short_description && short_description.trim() !== "" && (
                <div className="short-description-accordion mt-3">
                  <div
                    className="accordion-header d-flex justify-content-between align-items-center p-3"
                    style={{
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #dee2e6",
                      borderRadius: "5px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onClick={toggleShortDescription}
                  >
                    <span
                      style={{
                        fontFamily: "nexaBold",
                        color: "#212529",
                        fontSize: "16px",
                      }}
                    >
                      Quick Nuggets
                    </span>
                    <span>
                      {isShortDescriptionOpen ? (
                        <FaChevronUp style={{ color: "#249acd" }} />
                      ) : (
                        <FaChevronDown style={{ color: "#249acd" }} />
                      )}
                    </span>
                  </div>

                  <div
                    className="accordion-content"
                    style={{
                      maxHeight: isShortDescriptionOpen ? "1000px" : "0",
                      overflow: "hidden",
                      transition: "max-height 0.3s ease",
                      borderLeft: "1px solid #dee2e6",
                      borderRight: "1px solid #dee2e6",
                      borderBottom: isShortDescriptionOpen
                        ? "1px solid #dee2e6"
                        : "none",
                      borderBottomLeftRadius: "5px",
                      borderBottomRightRadius: "5px",
                    }}
                  >
                    <div
                      className="p-3"
                      style={{ fontFamily: "nexaLight", width: "100%" }}
                      dangerouslySetInnerHTML={{ __html: short_description }}
                    >
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case "Itinerary":
        return (
          <div className="custom-trips-details-itinerary">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="fs-6" style={{ color: "#249acd", margin: 0 }}>
                Itinerary Details
              </h4>
              {pdf_file && pdf_file.trim() !== "" ? (
                <button
                  onClick={handlePdfDownload}
                  className="custom-itinerary-pdf-btn d-flex align-items-center gap-2"
                >
                  <FaDownload style={{ fontSize: "16px" }} />
                  Download PDF
                </button>
              ) : (
                <p className="text-muted small">No PDF available</p>
              )}
            </div>
            <div
              className="custom-itineary-dangerous-html"
              dangerouslySetInnerHTML={{ __html: itinerary }}
            ></div>
          </div>
        );

      case "Inclusive-Exclusive":
        return (
          <div className="custom-trips-details-inclusive-exclusive pt-4 pb-2 px-3">
            <h6 style={{ color: "#249acd" }}>
              Destination: {title || "Unknown Destination"}
            </h6>
            <h6 className="m-0 pt-2" style={{ color: "#249acd" }}>
              What's Included
            </h6>
            {Array.isArray(inclusive) && inclusive.length > 0 ? (
              <ul>
                {inclusive.map((item, index) => (
                  <li key={index} style={{ fontFamily: "nexaLight" }}>
                    <span>
                      <FaCheckCircle
                        className="mx-2 text-success"
                        style={{ fontSize: "12px", marginBottom: "2px" }}
                      />
                    </span>
                    {safeGet(item, "inclusive", "No details available")}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted">No inclusions listed for this trip</p>
            )}

            <h6 className="m-0 pt-0" style={{ color: "#249acd" }}>
              Not Included
            </h6>
            {Array.isArray(exclusive) && exclusive.length > 0 ? (
              <ul>
                {exclusive.map((item, index) => (
                  <li
                    key={index}
                    style={{ fontFamily: "nexaLight" }}
                    className="d-flex"
                  >
                    <span>
                      <IoMdAddCircle
                        className="mx-2 text-danger"
                        style={{
                          transform: "rotate(45deg)",
                          fontSize: "15px",
                          marginBottom: "2px",
                        }}
                      />
                    </span>
                    {safeGet(item, "exclusive", "No details available")}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted">No exclusions listed for this trip</p>
            )}
          </div>
        );

      case "Batches":
        return (
          <div className="custom-trips-details-batches">
            <h6
              className="text-center pt-3 pb-1 fs-6"
              style={{ color: "#249acd" }}
            >
              Batches
            </h6>
            {Array.isArray(batches) && batches.length > 0 ? (
              batches.map((item, index) => {
                // Get month and short year (e.g., 'Oct-25') from the first batch date
                const monthFromBatch = safeGet(
                  item.batches[0],
                  "batche_from",
                  null
                );

                // *** MODIFIED: Use the new 'month-year' formatType for the button ***
                const monthDisplay = monthFromBatch
                  ? formatDate(monthFromBatch, "month-year")
                  : safeGet(item, "month", "TBD");

                const monthBatches = safeGet(item, "batches", []);

                return (
                  <Fragment key={index}>
                    <div className="custom-trips-details-batches-month text-center pb-4">
                      {/* Displays month as "Oct-25" */}
                      <button>{monthDisplay}</button>
                    </div>

                    <div className="container-fluid">
                      <div className="row">
                        {Array.isArray(monthBatches) &&
                        monthBatches.length > 0 ? (
                          monthBatches.map((ele) => {
                            const batchId = safeGet(ele, "id", Math.random());
                            const batch_status = safeGet(
                              ele,
                              "batch_status",
                              "Status Unknown"
                            );

                            // Dates inside the batch are still formatted as 'full' (DD-Mon-YYYY)
                            const batche_from_formatted = formatDate(
                              safeGet(ele, "batche_from"),
                              "full"
                            );
                            const batche_To_formatted = formatDate(
                              safeGet(ele, "batche_To"),
                              "full"
                            );

                            return (
                              <div
                                className="col-12 col-md-6 col-lg-6 col-xl-6 px-3 my-2 mb-3"
                                key={batchId}
                              >
                                <div className="custom-trips-details-batches-details col-lg-4 rounded-3 border border-1 border-secondary position-relative pt-3 pb-2 mb-3">
                                  <h6 className="text-center">
                                    {batch_status}
                                  </h6>
                                  <h3 className="pt-2">
                                    From : <span>{batche_from_formatted}</span>
                                  </h3>
                                  <h3>
                                    To : <span>{batche_To_formatted}</span>
                                  </h3>
                                  <div className="custom-trip-details-book-now-btn">
                                    <Link to={`/booknow/${slug || ""}`}>
                                      Book Now
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-muted text-center">
                            No batches available for this month
                          </p>
                        )}
                      </div>
                    </div>
                  </Fragment>
                );
              })
            ) : (
              <p className="text-muted text-center">
                No batches available for this trip
              </p>
            )}
          </div>
        );

      case "TermsAndConditions":
        return (
          <div className="custom-trips-details-terms-and-conditions">
            <div
              className="col-lg-12 custom-t-and-c-style py-2 px-3"
              // style={{ fontFamily: "nexaLight" }}
              dangerouslySetInnerHTML={{ __html: privacy_policy }}
            />
            {/* <SafeHTML 
                html={privacy_policy} 
                fallback="No terms and conditions available" 
              /> */}
          </div>
        );

      default:
        return (
          <div className="custom-trips-details-overview">
            <ul className="ps-0">
              <li style={{ fontFamily: "nexaLight" }}>
                <SafeHTML html={overview} fallback="No overview available" />
              </li>
              {short_description && short_description.trim() !== "" && (
                <>
                  <li
                    style={{
                      fontFamily: "nexaBold",
                      width: "100%",
                      color: "#212529",
                    }}
                  >
                    Short Description
                  </li>
                  <li style={{ fontFamily: "nexaLight", width: "100%" }}>
                    <SafeHTML html={short_description} />
                  </li>
                </>
              )}
            </ul>
          </div>
        );
    }
  };

  let dataToShow = handleTripsDetailsButton(activeTab);

  // Loading state
  if (isLoading) {
    return (
      <section className="custom-trips-details-section container-fluid">
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading trip details...</p>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="custom-trips-details-section container-fluid">
        <div className="alert alert-danger mt-4" role="alert">
          <strong>Error:</strong> {error}
          <button
            className="btn btn-outline-danger btn-sm ms-3"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="custom-trips-details-section container-fluid">
      <h2
        style={{ color: "#249acd", fontFamily: "nexabold" }}
        className="custom-trips-details-header pt-4 pb-3"
      >
        {title || "Trip Details"}
      </h2>
      <div className="row">
        <div className=" col-sm-12 col-lg-12 px-0 custom-trips-details-swipper">
          <Swiper
            slidesPerView={3}
            spaceBetween={30}
            loop={Array.isArray(galleryimage) && galleryimage.length > 1}
            // autoplay={Array.isArray(galleryimage) && galleryimage.length > 0 ? {
            //   delay: 3000,
            //   disableOnInteraction: false,
            // } : false}
            pagination={{
              clickable: true,
            }}
            navigation={true}
            modules={[Pagination, Navigation, Autoplay]}
            breakpoints={{
              320: { slidesPerView: 1, spaceBetween: 8 },
              375: { slidesPerView: 1, spaceBetween: 8 },
              425: { slidesPerView: 1, spaceBetween: 8 },
              640: { slidesPerView: 2, spaceBetween: 20 },
              768: { slidesPerView: 2, spaceBetween: 24 },
              1024: { slidesPerView: 3, spaceBetween: 30 },
            }}
            className="mySwiper custom-trips-details-swiper custom-trips-details-swiper-cont py-2"
          >
            {Array.isArray(galleryimage) && galleryimage.length > 0 ? (
              galleryimage.map((imgUrl, index) => (
                <SwiperSlide
                  className="custom-trips-slider-img"
                  key={index}
                  // style={{ overflow: "hidden", height: "650px" }}
                >
                  <img
                    className="img-fluid"
                    src={imgUrl || ""}
                    onError={(e) => {
                      // Hide the broken image and show fallback
                      e.target.style.display = "none";
                      const fallback = e.target.nextElementSibling;
                      if (fallback) {
                        fallback.style.display = "flex";
                      }
                    }}
                    alt={`${title || "Trip"} Image ${index + 1}`}
                    style={{
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                  {/* Fallback div for broken images */}
                  <div
                    style={{
                      display: "none",
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#f8f9fa",
                      border: "2px dashed #dee2e6",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#6c757d",
                      flexDirection: "column",
                    }}
                  >
                    <div style={{ fontSize: "3rem", marginBottom: "10px" }}>
                      🖼️
                    </div>
                    <p className="mb-0">Image not available</p>
                  </div>
                </SwiperSlide>
              ))
            ) : (
              <SwiperSlide className="w-100">
                <div
                  className="w-100"
                  style={{
                    height: "650px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f8f9fa",
                    border: "2px dashed #dee2e6",
                    color: "#6c757d",
                    flexDirection: "column",
                  }}
                >
                  <div style={{ fontSize: "3rem", marginBottom: "10px" }}>
                    🖼️
                  </div>
                  <p className="text-center mb-0">
                    No images available for this trip
                  </p>
                </div>
              </SwiperSlide>
            )}
          </Swiper>
        </div>
        <div className="col-lg-12 custom-trips-details-body mt-3 pb-3">
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12 col-lg-8 custom-trips-details-left-part px-0">
                <div className="custom-trips-details-button-container">
                  <button
                    className={
                      activeTab === "Overview"
                        ? "active-tab-btn"
                        : "inactive-tab-btn"
                    }
                    onClick={() => setActiveTab("Overview")}
                  >
                    Overview
                  </button>
                  <button
                    className={
                      activeTab === "Itinerary"
                        ? "active-tab-btn"
                        : "inactive-tab-btn"
                    }
                    onClick={() => setActiveTab("Itinerary")}
                  >
                    Itinerary
                  </button>
                  <button
                    className={
                      activeTab === "Inclusive-Exclusive"
                        ? "active-tab-btn"
                        : "inactive-tab-btn"
                    }
                    onClick={() => setActiveTab("Inclusive-Exclusive")}
                  >
                    Inclusive / Exclusive
                  </button>
                  <button
                    className={
                      activeTab === "Batches"
                        ? "active-tab-btn"
                        : "inactive-tab-btn"
                    }
                    onClick={() => setActiveTab("Batches")}
                  >
                    Batches
                  </button>
                  <button
                    className={
                      activeTab === "TermsAndConditions"
                        ? "active-tab-btn"
                        : "inactive-tab-btn"
                    }
                    onClick={() => setActiveTab("TermsAndConditions")}
                  >
                    Terms & Condition
                  </button>
                </div>
                <div className="custom-trips-details-button-content">
                  {dataToShow}
                </div>
              </div>
              <div className="col-md-12 col-lg-4 d-flex justify-content-center align-items-start custom-trips-details-right-part pe-1 container">
                <div
                  className="row container-fluid px-0 border border-primary"
                  style={{
                    border: "1px solid #84bffd",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  <div className="col-lg-12 custom-trips-details-right-part-buttons">
                    <div className="row">
                      <p className="col-lg-12">Price</p>
                    </div>
                    <div className="row align-items-center">
                      <div className="col-12 d-flex flex-column gap-2">
                        {bookingAmount > 0 && (
                          <label className="d-flex align-items-center gap-2">
                            <input
                              type="radio"
                              name="pricing"
                              checked={selectedPricing === "booking"}
                              onChange={() => setSelectedPricing("booking")}
                            />
                            Booking Amount :
                            <span style={{ fontWeight: 600 }}>
                              ₹{bookingAmount || "--"}
                            </span>
                          </label>
                        )}

                        <label className="d-flex flex-column">
                          <span className="d-flex align-items-center gap-2">
                            <input
                              type="radio"
                              name="pricing"
                              checked={selectedPricing === "full"}
                              onChange={() => setSelectedPricing("full")}
                            />
                            Full Package Amount :
                          </span>
                          <span
                            style={{
                              marginLeft: "2rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "1rem",
                            }}
                          >
                            {fullPackageAmount > 0 &&
                              discountedAmount !== fullPackageAmount && (
                                <span
                                  style={{
                                    textDecoration: "line-through",
                                    color: "#888",
                                  }}
                                >
                                  ₹{fullPackageAmount}
                                </span>
                              )}
                            <span style={{ fontWeight: 600, color: "#2491c5" }}>
                              ₹
                              {discountedAmount ||
                                fullPackageAmount ||
                                "Price on request"}
                            </span>
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-12 custom-trips-details-right-part-third-div px-0">
                    <ul className="px-3 mb-0">
                      <li>Prices are on per person basis.</li>
                      <li>GST @5% extra</li>
                    </ul>
                  </div>
                  <div className="col-lg-12 custom-trips-details-right-part-pricing-emi-plans px-4">
                    <div
                      className="row"
                      style={{
                        border: "2px solid #84bffd",
                        borderRadius: "10px",
                        overflow: "hidden",
                      }}
                    >
                    
                      {/* <div
                        className="px-2 py-2"
                        style={{
                          backgroundColor: "#edf7fb",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        EMI from 1120/month
                      </div> */}
                      <div className="row mx-0 px-0 py-1">
                        <div className="col-lg-6 fs-4 d-flex gap-2 justify-content-start align-items-center">
                          <SiPaytm />
                          <SiGooglepay />
                          <SiPhonepe />
                        </div>
                        {/* <div className="col-lg-6 text-end">
                          <a href="#">View Plans</a>
                        </div> */}
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-12 text-center px-3 py-2 d-flex justify-content-center align-items-center gap-3">
                    <Link
                      to="/tripdetailsform"
                      className="rounded-3 custom-trips-details-right-part-customize-trip-btn"
                    >
                      Customize Trips
                    </Link>
                    <Link
                      to={`/booknow/${slug || ""}`}
                      className="rounded-3 custom-trips-details-right-part-book-now-btn"
                      style={{ textWrap: "nowrap" }}
                    >
                      Book Now
                    </Link>
                    <a
                      href="https://wa.me/917011858174"
                      target="_blank"
                      rel="noopener noreferrer"
                      className=""
                    >
                      <img
                        src={WhatsappImg}
                        alt="WhatsApp Contact"
                        className="img-fluid custom-trip-details-whatsapp-img"
                        style={{ width: "55px" }}
                        onError={(e) => {
                          // Hide WhatsApp image if it fails to load
                          e.target.style.display = "none";
                        }}
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ReviewPreview />
      </div>
    </section>
  );
};

export default TripsDetails;
